import React from 'react';
import { cn } from '../../../utils';

export const LeftCard: React.FC<{
  backgroundImage: string;
  logo?: React.ReactNode;
  title?: string;
  subtitle?: string;
  /**
   * Adds a dark gradient scrim over the background image, for photos that
   * don't have enough natural contrast for a light logo/title. Defaults to a
   * top-to-bottom black gradient; pass overlayClassName for a different one.
   */
  overlay?: boolean;
  overlayClassName?: string;
  /**
   * Anchors the logo/title stack to the top or the bottom of the cover.
   * Defaults to the top.
   */
  logoPosition?: 'top' | 'bottom';
  /**
   * Renders a compact solid-color banner carrying just `logo`, visible only
   * below `lg` (where the cover photo below is `hidden`). Undefined (the
   * default) renders nothing here, so every caller that doesn't pass this
   * keeps today's mobile behavior - no LeftCard content at all below `lg`.
   */
  mobileHeaderClassName?: string;
}> = ({
  backgroundImage,
  logo,
  title,
  subtitle,
  overlay,
  overlayClassName,
  logoPosition = 'top',
  mobileHeaderClassName,
}) => {
  return (
    <>
      {mobileHeaderClassName && (
        // Full-bleed regardless of CardLayout's `container` padding AND its
        // sm/md max-width tiers (a plain negative margin only cancels the
        // padding, not the centered max-width gutter that appears between
        // `sm`/`md` and `lg`): `w-screen` + the left-1/2/-translate-x-1/2
        // pair re-centers a 100vw-wide box on the true viewport, independent
        // of any ancestor's width constraint. `self-start` pins it to the
        // top of its grid row rather than any inherited vertical centering.
        <div
          className={cn(
            'relative left-1/2 w-screen -translate-x-1/2 self-start lg:hidden',
            'mb-6 flex items-center justify-center p-8',
            mobileHeaderClassName
          )}
        >
          {logo}
        </div>
      )}
      <div
        className="relative hidden h-full flex-col p-10 text-white dark:border-r lg:flex filter brightness-75 bg-center"
        style={{
          background: `url(${backgroundImage})`,
          backgroundSize: 'cover',
        }}
      >
        {overlay && (
          <div
            className={cn(
              'absolute inset-0',
              overlayClassName ??
                'bg-gradient-to-b from-black/60 via-black/20 to-transparent'
            )}
          />
        )}
      </div>
      <div
        className={cn(
          'fixed hidden lg:flex lg:flex-col lg:gap-6 lg:max-w-[40vw] left-8',
          logoPosition === 'bottom' ? 'bottom-10' : 'top-10'
        )}
      >
        <div>{logo}</div>
        {title && (
          <div className="text-background text-5xl font-semibold">{title}</div>
        )}
        {subtitle && (
          <div className="text-background text-base font-medium mt-4">
            {subtitle}
          </div>
        )}
      </div>
    </>
  );
};
